import { Alert, Divider, Form, Input, Select, Switch, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { AgentSelect } from './AgentSelect'
import { SETTLEMENT_MODES } from './agentApi'

interface ResellerCreateFieldsProps {
  currency: string
  /** `user.create` — login va parol maydonlari shunga bog'liq */
  canCreateUser: boolean
  /** `agent.deposit.add` — depozit va qarz limiti shunga bog'liq */
  canFund: boolean
}

/**
 * Reseller qo'shish — agentnikidan BOSHQA forma.
 *
 * <h2>⚠️ FARQ QAYERDA</h2>
 * Reseller — SOTUVCHI: o'z shoxidagi abonentlarni sotadi va boshqaradi,
 * o'z narxini qo'yadi. Agent esa KASSIR: pul oladi, abonentni
 * tahrirlamaydi (ADR-029). Shuning uchun bu formada shox (ota tugun)
 * bor, pul bloki esa SHARTLI.
 *
 * <h2>⚠️ DEPOZIT FAQAT «KASSA» YOQILGANDA</h2>
 * Egasi qarori (2026-08-19): «agar reseller agent ham bo'lsa deposit
 * ko'rinadi bo'lmasa shartmas». Kassasiz reseller pul QABUL QILMAYDI va
 * bu serverda ham majburlanadi — `PartnerKind.RESELLER` huquqlari ichida
 * `agent.fund` YO'Q, u faqat `cash_desk` bayrog'i bilan qo'shiladi.
 * Ya'ni har resellerdan depozit so'rash operatorga bajarilmaydigan
 * va'da ko'rsatardi.
 *
 * ⚠️ Bayroq ilgari `kind = 'BOTH'` turi orqali so'ralardi. Tur
 * 2026-08-21 da olib tashlangach imkoniyat YO'QOLMADI: u shu yerdagi
 * alohida belgilagichga ko'chdi va serverga o'sha
 * `AgentOnboardRequest.cashDesk` maydoni bilan boradi.
 *
 * ⚠️ Abonent amallari chegarasi (o'chirish, nom/tarif/MAC almashtirish)
 * bu formada YO'Q, lekin RESELLER dan olib tashlanmagan: u tugun
 * tanlangandan keyin `AgentGovernance` da qo'yiladi. Yaratish paytida
 * to'rt sonni so'rash formani ikki barobar uzaytirardi, holbuki ular
 * standart holda cheksiz va odatda o'zgartirilmaydi.
 */
export function ResellerCreateFields({
  currency,
  canCreateUser,
  canFund,
}: ResellerCreateFieldsProps) {
  const { t } = useTranslation()

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('agents.account.resellerIntro')}
      />

      <Typography.Text strong>{t('agents.account.whoTitle')}</Typography.Text>
      <Divider style={{ margin: '8px 0 16px' }} />

      <Form.Item
        name="displayName"
        label={t('agents.displayName')}
        extra={t('agents.account.resellerNameHint')}
        rules={[{ required: true, message: t('agents.displayNameRequired') }]}
      >
        <Input autoFocus autoComplete="off" />
      </Form.Item>

      {canCreateUser ? (
        <>
          <Form.Item
            name="username"
            label={t('agents.account.login')}
            rules={[
              { required: true, message: t('agents.account.loginRequired') },
              { pattern: /^[a-zA-Z0-9._@-]{3,64}$/, message: t('agents.account.loginFormat') },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="rawPassword"
            label={t('agents.account.password')}
            extra={t('agents.account.passwordHint')}
            rules={[
              { required: true, message: t('agents.account.passwordRequired') },
              { min: 12, max: 72, message: t('agents.account.passwordLength') },
            ]}
          >
            <Input.Password visibilityToggle={false} autoComplete="new-password" />
          </Form.Item>
        </>
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('agents.account.noUserPermission')}
        />
      )}

      <Typography.Text strong>{t('agents.account.branchTitle')}</Typography.Text>
      <Divider style={{ margin: '8px 0 16px' }} />

      <Form.Item
        name="code"
        label={t('agents.code')}
        extra={t('agents.account.codeHint')}
        rules={[
          { required: true, message: t('agents.codeRequired') },
          { pattern: /^[a-zA-Z0-9._-]{2,64}$/, message: t('agents.codeFormat') },
        ]}
      >
        <Input autoComplete="off" />
      </Form.Item>

      <Form.Item
        name="parentId"
        label={t('agents.account.parentReseller')}
        extra={t('agents.account.parentResellerHint')}
      >
        <AgentSelect placeholder={t('agents.account.parentPlaceholder')} />
      </Form.Item>

      <Form.Item
        name="settlementMode"
        label={t('agents.settlementMode')}
        extra={t('agents.settlementHint')}
      >
        <Select
          options={SETTLEMENT_MODES.map((m) => ({ value: m, label: t(`settlementMode.${m}`) }))}
        />
      </Form.Item>

      {/* ⚠️ Savol ha/yo'q shaklida — u endi TUR emas, BAYROQ. Ilgari
          bu yerda «Reseller / Ikkalasi» tanlagichi turardi va u
          operatordan tur so'rab, aslida kassa bayrog'ini qo'yardi. */}
      <Form.Item
        name="cashDesk"
        label={t('agents.account.cashDesk')}
        extra={t('agents.account.cashDeskHint')}
        valuePropName="checked"
        initialValue={false}
      >
        <Switch />
      </Form.Item>

      {/* `shouldUpdate` argumentlari antd tomonidan `any` deb beriladi —
          ularni aniq tiplaymiz, aks holda `no-unsafe-member-access`
          ishga tushadi (`AgentsPage` dagi komissiya formasi bilan bir xil) */}
      <Form.Item
        noStyle
        shouldUpdate={(prev: { cashDesk?: boolean }, next: { cashDesk?: boolean }) =>
          prev.cashDesk !== next.cashDesk
        }
      >
        {({ getFieldValue }) =>
          getFieldValue('cashDesk') !== true ? null : canFund ? (
            <>
              <Typography.Text strong>{t('agents.account.moneyTitle')}</Typography.Text>
              <Divider style={{ margin: '8px 0 16px' }} />

              <Form.Item
                name="initialDeposit"
                label={t('agents.account.initialDeposit')}
                extra={t('agents.account.initialDepositHint')}
              >
                <MoneyInput currency={currency} minMinor={0} />
              </Form.Item>
              <Form.Item
                name="debtLimit"
                label={t('agents.debtLimit')}
                extra={t('agents.debtLimitHint')}
              >
                <MoneyInput currency={currency} minMinor={0} />
              </Form.Item>
              {/* ⚠️ Bitta to'lov shifti — `AgentCreateFields` dagi bilan
                  bir xil maydon va bir xil sabab. */}
              <Form.Item
                name="perTxnLimit"
                label={t('agents.account.perTxnLimit')}
                extra={t('agents.account.perTxnHint')}
              >
                <MoneyInput currency={currency} minMinor={0} />
              </Form.Item>
              <Form.Item
                name="dailyLimit"
                label={t('agents.account.dailyLimit')}
                extra={t('agents.account.fundLimitHint')}
              >
                <MoneyInput currency={currency} minMinor={0} />
              </Form.Item>
              <Form.Item name="monthlyLimit" label={t('agents.account.monthlyLimit')}>
                <MoneyInput currency={currency} minMinor={0} />
              </Form.Item>
            </>
          ) : (
            <Alert type="info" showIcon message={t('agents.account.noMoneyPermission')} />
          )
        }
      </Form.Item>
    </>
  )
}
